import { useEffect, useRef } from 'react'
import { fitWhenMapReady } from 'components/common/map/fitWhenMapReady'
import { buildMapFitBounds } from 'components/common/map/bounds'

const DEFAULT_PADDING = { top: 30, right: 30, bottom: 30, left: 30 }

// Auto-fits map bounds once the RTKQ query is successful AND the map projection
// is ready. Uses `fitWhenMapReady` under the hood so WebGL overlays (deck.gl)
// are guaranteed to pick up the viewport change via a 1-pixel panBy nudge.
//
// For custom bounds shapes (multi-source, breadcrumbs, etc.), pass a `fitFn`
// instead of relying on the built-in data/lat/lng/radius computation.
//
// `refitKey` MUST encode latitude/longitude (and any other inputs that change
// the bounds) so a customer/location switch produces a new key and re-fits.
export const useFitBoundsWhenReady = ({
  map,
  mapsLib,
  isSuccess,
  data,
  latitude,
  longitude,
  radius,
  padding = DEFAULT_PADDING,
  refitKey,
  disabled = false,
  fitFn,
}) => {
  const fittedKeyRef = useRef(null)

  useEffect(() => {
    if (disabled) return undefined
    if (!map || (!fitFn && !mapsLib)) return undefined
    if (!isSuccess) return undefined
    if (fittedKeyRef.current === refitKey) return undefined

    const defaultFit = () => {
      const result = buildMapFitBounds({ mapsLib, data, latitude, longitude, radius })
      if (!result) return
      const { bounds, hasMarkers, centerValid } = result

      // Empty bounds → fitBounds would pan to (0,0) / world view. Bail.
      if (hasMarkers || (radius && centerValid)) {
        map.fitBounds(bounds, padding)
      } else if (centerValid) {
        map.setCenter({ lat: latitude, lng: longitude })
        map.setZoom(17)
      }
    }

    return fitWhenMapReady(map, () => {
      if (fitFn) fitFn()
      else defaultFit()
      fittedKeyRef.current = refitKey
    })
  }, [map, mapsLib, isSuccess, data, latitude, longitude, radius, padding, refitKey, disabled, fitFn])
}
